/**
 * static-sitemap-manifest — what static.xml advertises, and where each page's
 * <lastmod> honestly comes from.
 *
 * FRIDGE-249. static.xml used to stamp one literal date on every page here
 * (2026-02-09, then 2026-07-17 — bumped by hand when someone remembered). The
 * literal was wrong in both directions: /deals and /products change with the
 * catalog every day, /privacy-policy had not changed in weeks. A file's git
 * commit date is not available at runtime on Vercel, so the truth is recorded
 * here once and GUARDED: tests/sitemap-lastmod.test.ts reads git for every
 * `code` entry and fails when a listed file was authored after the entry's
 * lastmod. Bump the entry in the same change that edits the content — the
 * guard is what makes the date believable.
 *
 * Source kinds:
 *   - code        content lives in the listed files; lastmod = the recorded
 *                 date (day granularity), guarded by git author dates. `files`
 *                 lists the route and the modules that ARE its content (client
 *                 component, summary module, the committed dataset JSON) —
 *                 shared chrome (header/footer/schemas) is boilerplate and
 *                 deliberately excluded.
 *   - catalog     the page renders from the live product catalog; lastmod =
 *                 newest products.updated_at over active PDP-eligible rows.
 *   - collections lastmod = newest collections.updated_at over active indexed
 *                 rows (what /collections/all lists).
 *   - press       lastmod = newest press_releases published_at/updated_at over
 *                 published rows.
 *   - tools       lastmod = newest tools.updated_at over implemented tools
 *                 that ship a widget (what /widgets lists).
 *   - recalls     lastmod = newest curated cpsc_recalls change event (ingest
 *                 created_at / CPSC last_publish_date) — what
 *                 /data/recall-statistics renders live at request time.
 *   - snapshots   lastmod = the newest stored price reading (FRIDGE-903): every
 *                 figure on /deals and the Black Friday hub is derived from the
 *                 snapshot table, so the day a reading lands is the day the
 *                 page changed — and the page's own node states the newest
 *                 reading it rendered.
 *   - parts       lastmod = newest products.updated_at over the TRUE-part rows
 *                 (/parts lists parts only; `catalog` would credit it with
 *                 every appliance revision).
 * A live kind whose read fails emits NO lastmod for that page (never a guess).
 *
 * The path list itself is pinned by the guard test — this refactor moves no
 * URL in or out of the family.
 */

export type StaticSitemapEntry =
  | { path: string; source: 'code'; lastmod: string; files: string[] }
  | { path: string; source: 'catalog' | 'collections' | 'press' | 'tools' | 'recalls' | 'snapshots' | 'parts' }

const PRICE_INDEX_FILES = ['app/data/_data/price-index.json', 'components/seo/data-index-summary.ts']

export const STATIC_SITEMAP_MANIFEST: readonly StaticSitemapEntry[] = [
  { path: '/editorial-standards', source: 'code', lastmod: '2026-09-24', files: ['app/editorial-standards/page.tsx', 'lib/editorial-standards.ts'] },
  { path: '', source: 'catalog' }, // Home — featured catalog, deals, prices
  {
    path: '/about-us',
    source: 'code',
    // 08/22/2026 — the owner's canonical identifier definitions became THE
    // text on every definitional surface (lib/identifiers.ts; "no different
    // versions"): footer, hub cards, spokes, FAQ answer, JSON-LD DefinedTerms.
    // 08/21/2026 (owner rulings, second pass) — Authors became its own
    // #authors section with the four real person authors (photo, name, bio)
    // read live from the authors table; the sterile organizational byline came
    // off the page; "store" language became "catalog" sitewide (Fridge.com
    // sells nothing); the retailer claim moved to RETAILER_INTEGRATIONS_DISPLAY
    // ("25+"); the four systems were split into three identifiers + one score;
    // the CTA body paragraph was removed.
    // 08/21/2026 — "How our data works" grew from two proprietary systems to
    // the four-card FIN/FPN/FFN/FIS grid; Parts + Filter joined Explore; the
    // stale dated freshness claim was dropped.
    // 08/17/2026 — "We monitor N retailers" now uses RETAILER_INTEGRATIONS (25,
    // what we are integrated with) instead of RETAILERS_TRACKED (15, what has a
    // live in-stock offer right now). It had been using the live-inventory
    // figure for a capability claim and under-stated the company by nine.
    // 08/16/2026 — the About block now links /about-us/limitations (FRIDGE-295).
    // 08/27/2026 — FRIDGE-575 plain-language pass ("units cataloged", was
    // "indexed").
    // 08/28/2026 — FRIDGE-602: the verified trust row joined the stats
    // section (four measured signals with derivations).
    // 08/29/2026 — FRIDGE-628 hero declutter: every number renders once, the
    // two stat bands merged into one labeled 4×2 system, checkmarks + share
    // on one row.
    // 08/30/2026 — owner direction "too many boxes": the eight stat cells
    // become four. The trust four keep their band; the catalog four become one
    // line of linked figures. Every number still renders exactly once.
    // 08/31/2026 — FRIDGE-690: the owner's new display set (6,000+ products)
    // through the one registry; the page's two hardcoded counts now
    // interpolate SITE_STATS.
    // 09/05/2026 — FRIDGE-859: the publisher logo in this page's JSON-LD
    //   pointed at a file that has never existed in public/.
    // 2026-09-16 — FRIDGE-452: the brand-store list in app/about-us/about-client.tsx now ends with "and more".
    // 09/17/2026 — FRIDGE-1060: the wine-coolers link in the collections list now points at the live wine-fridges collection (the old target was a redirect source).
    // 2026-09-21 — the browser-safe image exclusion runtime changed the About
    // page's shared product-card dependency graph.
    lastmod: '2026-09-24',
    files: ['app/about-us/page.tsx', 'app/about-us/about-client.tsx', 'lib/site-stats.ts', 'lib/editorial-standards.ts'],
  },
  {
    // 08/21/2026 — the identifier hub-and-spoke build (owner ruling: /about-us
    // is the hub; one exact-match URL per system). /about-us/fin was /about/fin
    // and was in NO sitemap at all until today; /about/fin now 308s here.
    path: '/about-us/fin',
    source: 'code',
    // 08/27/2026 — the ASIN example line now reads "a listing code" (the
    // owner's supplier-names-never-onsite rule).
    // 09/04/2026 — lib/seo/fin-parts-jsonld.ts (shared with this page) gained the
    // filter-hub builders (FRIDGE-722 · FRIDGE-724); this page's own nodes are unchanged.
    // 09/05/2026 — FRIDGE-855: the ONE Organization, referenced by @id.
    //   the shared fin-parts JSON-LD builder behind this hub stated its
    //   Organization as a literal; it now references the one site entity by @id.
    // 09/13/2026 — the shared systems table dropped its two `rowgroup` roles and
    //   documented why `role="cell"` stays (measured in a Chromium accessibility
    //   tree under the phone card CSS); rendered markup of this page changed.
    // 09/14/2026 — lib/seo/fin-parts-jsonld.ts (shared with this page) gained the
    // filter cross-reference builder (FRIDGE-731); this page's own nodes are unchanged.
    // 2026-09-26 — FRIDGE-1138: the <title> follows the shared length policy
    //   ("What is a FIN? Fridge.com Identification Number | Fridge.com"); no
    //   Bing AI citations are recorded for this URL (snapshot 07/09/2026).
    // 2026-10-09 — FRIDGE-1414: lib/identifiers.ts (shared with this hub) gained the FIS
    //   token rule (fisToken, FIS_TOKEN_RULE); the hub's own nodes are unchanged.
    lastmod: '2026-10-09',
    files: ['app/about-us/fin/page.tsx', 'lib/seo/fin-parts-jsonld.ts', 'app/about-us/_components/systems-table.tsx', 'lib/identifiers.ts', 'lib/brand-facts.ts', 'lib/fis-factors.ts'],
  },
  {
    path: '/about-us/fpn',
    source: 'code',
    // 09/04/2026 — lib/seo/fin-parts-jsonld.ts (shared with this page) gained the
    // filter-hub builders (FRIDGE-722 · FRIDGE-724); this page's own nodes are unchanged.
    // 09/05/2026 — FRIDGE-855: the ONE Organization, referenced by @id.
    //   the shared fin-parts JSON-LD builder behind this spoke stated its
    //   Organization as a literal; it now references the one site entity by @id.
    // 09/13/2026 — shared systems table markup changed (see the /about-us/fin note).
    // 09/14/2026 — lib/seo/fin-parts-jsonld.ts (shared with this page) gained the
    // filter cross-reference builder (FRIDGE-731); this page's own nodes are unchanged.
    // 2026-10-09 — FRIDGE-1414: lib/identifiers.ts (shared with this spoke) gained the FIS
    //   token rule; this page's own nodes are unchanged.
    lastmod: '2026-10-09',
    files: ['app/about-us/fpn/page.tsx', 'app/about-us/_components/identifier-spoke.tsx', 'lib/seo/fin-parts-jsonld.ts', 'app/about-us/_components/systems-table.tsx', 'lib/identifiers.ts', 'lib/brand-facts.ts', 'lib/fis-factors.ts'],
  },
  {
    path: '/about-us/ffn',
    source: 'code',
    // 09/04/2026 — lib/seo/fin-parts-jsonld.ts (shared with this page) gained the
    // filter-hub builders (FRIDGE-722 · FRIDGE-724); this page's own nodes are unchanged.
    // 09/05/2026 — FRIDGE-855: the ONE Organization, referenced by @id.
    //   the shared fin-parts JSON-LD builder behind this spoke stated its
    //   Organization as a literal; it now references the one site entity by @id.
    // 09/13/2026 — shared systems table markup changed (see the /about-us/fin note).
    // 09/14/2026 — lib/seo/fin-parts-jsonld.ts (shared with this page) gained the
    // filter cross-reference builder (FRIDGE-731); this page's own nodes are unchanged.
    // 2026-10-09 — FRIDGE-1414: lib/identifiers.ts (shared with this spoke) gained the FIS
    //   token rule; this page's own nodes are unchanged.
    lastmod: '2026-10-09',
    files: ['app/about-us/ffn/page.tsx', 'app/about-us/_components/identifier-spoke.tsx', 'lib/seo/fin-parts-jsonld.ts', 'app/about-us/_components/systems-table.tsx', 'lib/identifiers.ts', 'lib/brand-facts.ts', 'lib/fis-factors.ts'],
  },
  {
    path: '/about-us/fis',
    source: 'code',
    // 09/04/2026 — lib/seo/fin-parts-jsonld.ts (shared with this page) gained the
    // filter-hub builders (FRIDGE-722 · FRIDGE-724); this page's own nodes are unchanged.
    // 09/05/2026 — FRIDGE-855: the ONE Organization, referenced by @id.
    //   the shared fin-parts JSON-LD builder behind this spoke stated its
    //   Organization as a literal; it now references the one site entity by @id.
    // 09/13/2026 — shared systems table markup changed (see the /about-us/fin note).
    // 09/14/2026 — lib/seo/fin-parts-jsonld.ts (shared with this page) gained the
    // filter cross-reference builder (FRIDGE-731); this page's own nodes are unchanged.
    // 2026-10-09 — FRIDGE-1414: the page prints the FIS token rule (FIS-78701, FIS-TX,
    //   FIS-TX-AUSTIN) beside the score definition; lib/identifiers.ts gained the rule.
    lastmod: '2026-10-09',
    files: ['app/about-us/fis/page.tsx', 'app/about-us/_components/identifier-spoke.tsx', 'lib/seo/fin-parts-jsonld.ts', 'app/about-us/_components/systems-table.tsx', 'lib/identifiers.ts', 'lib/brand-facts.ts', 'lib/fis-factors.ts'],
  },
  {
    // 08/21/2026 — MOVED from /about/limitations. The owner retired the old
    // `about` namespace outright ("we are at /about-us/"), so the editorial
    // trio joins the identifier spokes beneath the one about root; the old URL
    // 308s here in both redirect layers.
    // 08/21/2026 — the FIN-explainer link re-aimed to /about-us/fin (hub-and-spoke).
    path: '/about-us/limitations',
    source: 'code',
    // 08/16/2026 — new page: the standing known-limitations and data-gaps
    // disclosure (FRIDGE-295). Counts on it are re-queried at each review, so
    // the date here is the review date, which is what the page prints.
    // 08/17/2026 — copy reframed on the owner's direction (FRIDGE-332): every
    // limitation stated as the design choice behind it; counts unchanged.
    // 08/27/2026 — review pass (FRIDGE-575/576): "indexed" jargon replaced with
    // plain language, collection and article-family counts re-measured live.
    // 09/05/2026 — FRIDGE-855: the ONE Organization, referenced by @id.
    //   the page's isPartOf WebSite and its `about` Organization were both
    //   literals with no @id; both now reference the one site entity.
    lastmod: '2026-09-22',
    files: ['app/about-us/limitations/page.tsx'],
  },
  {
    // 08/21/2026 — MOVED from /about/methodology, and ADDED to the sitemap for
    // the first time. Like /about/fin before it, this page has always been
    // `robots: index, follow` while sitting in no sitemap at all — the same
    // orphaning the hub-and-spoke build found and closed. Moving it was the
    // moment to fix that too.
    path: '/about-us/methodology',
    source: 'code',
    // 08/22/2026 — retailer lists now end with "and more", and the cross-retailer
    // paragraph was corrected: it named Amazon as tier-1, which the widget's own
    // TIER_1_RETAILERS set excludes (FRIDGE-452).
    // 09/05/2026 — FRIDGE-855: the ONE Organization, referenced by @id.
    //   the page's JSON-LD publisher was a literal Organization with no @id;
    //   it now references the one site entity.
    // 2026-09-16 — FRIDGE-769: the AI-citations definition and its WebPageElement landed on the methodology page.
    lastmod: '2026-09-22',
    files: ['app/about-us/methodology/page.tsx'],
  },
  {
    // 08/21/2026 — MOVED from /about/data-sources, and ADDED to the sitemap for
    // the first time, for the same reason as /about-us/methodology above.
    path: '/about-us/data-sources',
    source: 'code',
    // 08/22/2026 — retailer enumeration now ends with "and more" (FRIDGE-452).
    // 09/05/2026 — FRIDGE-855: the ONE Organization, referenced by @id.
    //   the page's JSON-LD publisher was a literal Organization named Fridge.com
    //   with no @id — an anonymous lookalike to any consumer that merges on @id.
    //   It now references the one site entity.
    lastmod: '2026-09-22',
    files: ['app/about-us/data-sources/page.tsx'],
  },
  {
    path: '/blogs/news/tags',
    source: 'code',
    // 08/16/2026 — lib/blog-tags.ts gained the body-copy brand boundary rule
    // (FRIDGE-321), which changes what these pages render.
    // 09/05/2026 — FRIDGE-859: the publisher logo in this page's JSON-LD
    //   pointed at a file that has never existed in public/.
    // 2026-09-16 — FRIDGE-859: the og:image now resolves to a real file.
    // 2026-09-26 — FRIDGE-1256: the page is wrapped in the fail-fast render
    //   (a cancelled database statement now errors instead of serving a page
    //   built from holes); no copy changed.
    // 2026-09-27 — FRIDGE-1256 withdrawn before release (optional reads became
    //   500s on the PR preview); the page renders as it did before; no copy changed.
    // 2026-09-29 — FRIDGE-1256: the route declares a 60 s render ceiling; no copy changed.
    lastmod: '2026-09-29',
    files: ['app/blogs/news/tags/page.tsx', 'lib/blog-tags.ts'],
  },
  { path: '/collections/all', source: 'collections' },
  { path: '/products', source: 'catalog' }, // paginated catalog index (PDPs live in products.xml)
  {
    path: '/contact',
    source: 'code',
    // 08/28/2026: the press-contact link label settled on "press section"
    // (the owner's final ruling: URL + breadcrumb + H1 always match, so the
    // section label is "Press", matching the immutable /press URL).
    // Prior: 08/27 AA-passing orange submit (FRIDGE-142) + per-IP rate limit (FRIDGE-151).
    lastmod: '2026-10-05',
    files: ['app/contact/page.tsx', 'app/contact/contact-form.tsx'],
  },
  // /developers REMOVED 08/28/2026 (owner ruling, same day it entered this
  // manifest): the machine-interface documentation page is withdrawn pending
  // the abuse-surface rethink (FRIDGE-600). The route 308s to /about-us in
  // next.config.js. Never re-add without the owner's explicit revival.
  // /faq 08/16/2026 — the calculator-results answer now describes what runs
  // (FRIDGE-293) and the FAQ figures come from data (FRIDGE-257).
  // 08/21/2026 — new entry "What are FIN, FPN, FFN, and FIS?" + brand count aligned to the constant.
  // 08/31/2026 — FRIDGE-690: both count-carrying answers now interpolate
  // SITE_STATS (6,000+ products / 500+ brands).
  // /faq 09/05 (FRIDGE-855): the FAQPage's publisher was a literal Organization
  // with no @id; it now references the one site entity.
  { path: '/faq', source: 'code', lastmod: '2026-09-18', files: ['app/faq/page.tsx'] },
  { path: '/press', source: 'press' },
  // /privacy-policy 08/16/2026 — section 4 rewritten to what actually runs
  // (FRIDGE-293), then the consent choice control added beside it (FRIDGE-115).
  // The anonymous error-impact disclosure changed on 2026-09-23 (UTC); the
  // retention wording was corrected on 2026-09-30.
  { path: '/privacy-policy', source: 'code', lastmod: '2026-09-30', files: ['app/privacy-policy/page.tsx', 'app/privacy-policy/policy-2026.tsx'] },
  {
    path: '/terms-of-service',
    source: 'code',
    // 08/16/2026 — the affiliate-disclosure paragraph no longer claims
    // "independent testing and evaluation" the site does not do (FRIDGE-114).
    lastmod: '2026-09-20',
    files: ['app/terms-of-service/page.tsx'],
  },
  { path: '/widgets', source: 'tools' },
  {
    path: '/compare',
    source: 'code',
    // 08/17/2026: the accessibility landmark pass (f7365e16) edited this page
    // and the two below without bumping their lastmod, so the sitemap was
    // advertising a stale date for changed pages. The git guard in
    // tests/sitemap-lastmod.test.ts caught it.
    // 2026-08-28: the sitewide muted-text contrast sweep (FRIDGE-606, gray-400
    // class lifted to gray-500) changed this page's rendered bytes.
    // 2026-09-05: FRIDGE-872 rebuilt the page in the homepage-2026 design
    // language — house product cards for the compared products, a live product
    // rail in the empty state, the spec table with best-value highlights. The
    // file list grew with the page: the card, the payload mapping and the rail
    // query all change what /compare serves, so all three are watched here.
    // 2026-09-16 — FRIDGE-814: the compare card's CTA opens the product page first (app/compare/compare-card.tsx).
    // 2026-09-26 — FRIDGE-1256: page.tsx is wrapped in the fail-fast render; no copy changed.
    // 2026-09-27 — FRIDGE-1256 withdrawn before release; page.tsx renders as before; no copy changed.
    // 2026-09-29 — FRIDGE-1256: the route declares a 60 s render ceiling; no copy changed.
    // 2026-09-30 — lib/compare-picks.ts: the empty-state rail read is isolated from a
    //   cancelled statement (an empty rail instead of a failed page); no copy changed.
    // 2026-10-06 — FRIDGE-902: the Samsung and LG answer prints its question and
    //   the sentence moves to lib/compare-answers.ts, shared with the FAQPage node.
    // 2026-10-12 (UTC day of the commit) — FRIDGE-1463: the hero paragraph opens with
    //   "Compare refrigerator prices and specs side by side" (page.tsx).
    lastmod: '2026-10-12',
    files: [
      'app/compare/page.tsx',
      'app/compare/samsung-lg-answer.tsx',
      'lib/compare-answers.ts',
      'app/compare/compare-client.tsx',
      'app/compare/compare-empty-state.tsx',
      'app/compare/compare-card.tsx',
      'app/compare/compare-product.ts',
      'lib/compare-picks.ts',
    ],
  },
  { path: '/deals', source: 'snapshots' }, // live price-drop hub — derived from the stored readings
  { path: '/black-friday-refrigerator-deals', source: 'snapshots' }, // deal verdicts over the same stored readings
  {
    path: '/data',
    source: 'code',
    // 08/22/2026 — FRIDGE-450: the charts and tables on this page became
    // interactive (hover/focus marks, keyboard-reachable, real links), so the
    // rendered page genuinely changed. 08/21 was the /about-us link re-aim.
    // 2026-08-28: muted-text contrast sweep (FRIDGE-606) touched app/data/page.tsx.
    // 2026-08-31: FRIDGE-673 (commit 0af1f52a) added the recall-statistics
    // report to the /data portal — app/data/page.tsx changed with it.
    // 2026-09-05: FRIDGE-906 added the embed-and-feeds card to the portal —
    // app/data/page.tsx changed again, so the advertised date moves with it.
    lastmod: '2026-09-20',
    files: ['app/data/page.tsx', ...PRICE_INDEX_FILES],
  },
  {
    // 08/21/2026 — the five monthly-series tables became sortable (FRIDGE-450).
    // 09/05/2026 — FRIDGE-906 put the price-index feed's autodiscovery link in
    // this page's head, which changes the document a crawler receives.
    path: '/data/price-index',
    source: 'code',
    // 09/17/2026 — FRIDGE-1031: a phone-only swipe cue renders above every series table (series-tables.tsx).
    // 2026-10-06 — FRIDGE-902: the answer sentence leads the FAQ list and its FAQPage node.
    lastmod: '2026-10-06',
    // FRIDGE-902: dated source-derived answer added above the report.
    files: ['app/data/_components/answer-sentences.ts', 'app/data/price-index/page.tsx', 'app/data/_components/series-tables.tsx', ...PRICE_INDEX_FILES],
  },
  {
    path: '/data/price-index/archive',
    source: 'code',
    // 08/22/2026 — the CSV anchor carries an explained lint exemption; the target
    // is a Route Handler serving an attachment, not a page (FRIDGE-337).
    // 09/05/2026 — FRIDGE-859: the publisher logo in this page's JSON-LD
    //   pointed at a file that has never existed in public/.
    lastmod: '2026-09-20', // FRIDGE-859: the og:image now resolves to a real file
    files: ['app/data/price-index/archive/page.tsx', ...PRICE_INDEX_FILES],
  },
  {
    path: '/data/brand-index',
    source: 'code',
    // 09/04/2026 — FRIDGE-766: the split-dollar rewrite made every price on the
    // page one text node, so a crawler reading innerText no longer sees "$ 50".
    // 2026-10-12 (UTC day of the commit) — FRIDGE-1463: brand-index-summary.ts gained the
    // certified-model leaders helper that /data/most-efficient prints; no copy on this
    // page changed.
    lastmod: '2026-10-12',
    files: [
      'app/data/brand-index/page.tsx',
      'app/data/brand-index/brand-index-summary.ts',
      'app/data/brand-index/brand-tables.tsx',
      'app/data/_data/brand-index.json',
    ],
  },
  {
    path: '/data/price-by-size',
    source: 'code',
    lastmod: '2026-09-16',
    files: [
      'app/data/price-by-size/page.tsx',
      'app/data/price-by-size/price-by-size-summary.ts',
      'app/data/price-by-size/price-by-size-tables.tsx',
      'app/data/_data/price-by-size.json',
    ],
  },
  {
    path: '/data/capacity-trends',
    source: 'code',
    lastmod: '2026-09-16',
    files: [
      'app/data/capacity-trends/page.tsx',
      'app/data/capacity-trends/capacity-trends-summary.ts',
      'app/data/capacity-trends/capacity-tables.tsx',
      'app/data/_data/capacity-trends.json',
    ],
  },
  {
    path: '/data/wine-beverage-index',
    source: 'code',
    // 08/22/2026 — FRIDGE-450: both hand-rolled tables now render through
    // LeagueTable, so every column sorts and the entity names are real links.
    // 09/04/2026 — FRIDGE-766: split-dollar prices folded into one text node.
    // 09/17/2026 — FRIDGE-1060: both wine-coolers links repointed at the live wine-fridges collection.
    // 10/08/2026 — FRIDGE-1450/1452: the beverage-fridges and beverage-centers entries folded into beverage-coolers.
    lastmod: '2026-10-08',
    files: [
      'app/data/wine-beverage-index/page.tsx',
      'app/data/wine-beverage-index/wine-beverage-summary.ts',
      'app/data/wine-beverage-index/wine-tables.tsx',
      'app/data/_data/wine-beverage-index.json',
    ],
  },
  {
    path: '/data/most-efficient',
    source: 'code',
    // 2026-08-25: leaderboard-tables.tsx and page.tsx changed in the FRIDGE-369
    // contrast fix (#c05a12 -> #b25410). The git guard in tests/sitemap-lastmod.test.ts
    // caught the stale stamp — a page that changed must not advertise an older lastmod.
    // 2026-08-28: muted-text contrast sweep (FRIDGE-606) touched leaderboard-tables.tsx.
    // 2026-10-08 (UTC day of the commit): the lede opens with the dated per-cubic-foot
    // leaders (FRIDGE-1463); page.tsx and most-efficient-summary.ts changed.
    // 2026-10-12 (UTC day of the commit): the lede adds the brand-count sentence from the
    // Brand Index snapshot (FRIDGE-1463); page.tsx changed.
    lastmod: '2026-10-12',
    files: [
      'app/data/most-efficient/page.tsx',
      'app/data/most-efficient/most-efficient-summary.ts',
      'app/data/most-efficient/leaderboard-tables.tsx',
      'app/data/_data/most-efficient.json',
    ],
  },
  {
    path: '/data/space-efficiency',
    source: 'code',
    // 08/22/2026 — FRIDGE-450: the league table now sorts on every column.
    lastmod: '2026-09-16',
    files: [
      'app/data/space-efficiency/page.tsx',
      'app/data/space-efficiency/space-efficiency-summary.ts',
      'app/data/_data/space-efficiency.json',
    ],
  },
  {
    path: '/data/dimensions',
    source: 'code',
    // 08/22/2026 — FRIDGE-450: the league table now sorts on every column.
    // 2026-10-06 — FRIDGE-902: the dimensions and weight sentences lead the FAQ list and its FAQPage node.
    lastmod: '2026-10-06',
    // FRIDGE-902: dated source-derived answer added above the report.
    files: ['app/data/_components/answer-sentences.ts', 'app/data/dimensions/page.tsx', 'app/data/dimensions/dimensions-summary.ts', 'app/data/_data/dimensions.json'],
  },
  // 08/31/2026 — FRIDGE-673: the recall-statistics report. Every figure on it
  // is a live query over the curated cpsc_recalls record, so its honest
  // lastmod is the record's newest change event, not a hand-recorded code
  // date (which would also go stale silently as recalls land).
  { path: '/data/recall-statistics', source: 'recalls' },
  {
    // 09/05/2026 — FRIDGE-906: the publisher embed-and-feeds page. Its content
    // is the two registries (feeds and widgets) plus its own copy, so a `code`
    // lastmod guarded by git author dates is the honest kind: the page changes
    // exactly when one of those files changes. The FEEDS the page documents
    // are NOT sitemap entries — they are machine endpoints, §1.7.
    path: '/data/embed',
    source: 'code',
    // 09/07/2026 — the declared publisher-feed dependency changed with the
    // Pinterest feed classification. Record its committed code revision;
    // this does not claim that Pinterest publishing has been activated.
    // 10/06/2026 — publisher-feeds.ts gained the per-board recovery feed
    // exemption (FRIDGE-1217).
    lastmod: '2026-10-06',
    files: [
      'app/data/embed/page.tsx',
      'lib/feeds/publisher-feeds.ts',
      'lib/embed/assets.ts',
      'components/data/embed-this-card.tsx',
    ],
  },
  {
    path: '/data/methodology',
    source: 'code',
    // 08/16/2026 — added the known-limitations card (FRIDGE-295).
    // 08/21/2026 — its /about/* links re-aimed at /about-us/* (namespace retired).
    // 08/22/2026 — same CSV-anchor exemption as the archive page (FRIDGE-337).
    // 08/28/2026 — FRIDGE-604 copy pass: the database-table parenthetical
    // became reader language ("does not yet hold a multi-retailer series").
    lastmod: '2026-09-20',
    files: ['app/data/methodology/page.tsx', ...PRICE_INDEX_FILES],
  },
  { path: '/parts', source: 'parts' }, // parts catalog over the TRUE-part rows (FRIDGE-501 plural; parts.xml carries only the product URLs)
  // NO '/filters' row here — the filter family home rides app/sitemap/filters.xml
  // (its index `<loc>` plus the editorial hubs), exactly as the editorial index
  // always did. One sitemap membership per URL; the retired /filter store hub's
  // static membership ended with the root (FRIDGE-500).
  // /financing 08/28: muted-text contrast sweep (FRIDGE-606) touched the page.
  // /financing 09/05 (FRIDGE-855): the page's isPartOf WebSite and its publisher
  // Organization were both literals with no @id; both now reference the one site entity.
  // 2026-09-16 — FRIDGE-804: the financing guide dropped every illustrative monthly-payment figure; readers use the calculators with their own terms.
  { path: '/financing', source: 'code', lastmod: '2026-09-22', files: ['app/financing/page.tsx'] },
  // /warranty 09/05 (FRIDGE-855): the page's isPartOf WebSite and its publisher
  // Organization were both literals with no @id; both now reference the one site entity.
  // 2026-09-17 — FRIDGE-1039: the ranked home-warranty comparison (five providers, published
  // plan facts only, call-to-action slots empty until a program is approved) joined the hub.
  { path: '/warranty', source: 'code', lastmod: '2026-09-26', files: ['app/warranty/page.tsx', 'lib/warranty/programs-fixture.json', 'lib/warranty/maker-links.ts'] },
  // /retailer-policies 09/04 (FRIDGE-803): delivery, haul-away, installation, returns, restocking and price-match at every compared retailer.
  // FRIDGE-870: authored customer-copy updates are reflected in code-backed dates.
  { path: '/retailer-policies', source: 'code', lastmod: '2026-09-22', files: ['app/retailer-policies/page.tsx', 'lib/retailer-policies.ts'] },
  { path: '/sitemap', source: 'catalog' }, // HTML sitemap hub — its counts move with the catalog
]

/** The advertised paths, in sitemap order. */
export const STATIC_SITEMAP_PATHS: readonly string[] = STATIC_SITEMAP_MANIFEST.map((e) => e.path)
